import { SRS_RANK } from '@/constants';
import { LEGACY_DEVICE_ID } from './deviceId';
import type { AttemptBucket, WordProgress, WordProgressMap } from '@/types';

// ============================================
// Field-level SRS progress merge
// ============================================

/**
 * Upper bound for nextReview (ms since epoch).
 * Clamps far-future values from clock-skewed devices.
 * 400 days from a notional "now" — generous enough not to disturb normal
 * 30-day mastered intervals while still bounding extreme clock drift.
 */
const MAX_NEXT_REVIEW_OFFSET_MS = 400 * 24 * 60 * 60 * 1000; // 400 days in ms

function clampNextReview(value: number): number {
  const ceiling = Date.now() + MAX_NEXT_REVIEW_OFFSET_MS;
  if (!isFinite(value) || value < 0) return 0;
  return value > ceiling ? ceiling : value;
}

// ============================================
// Attempt-bucket normalization (deficit fold)
// ============================================

/** NaN / non-finite / negative → 0; fractional → floor. Counters are counts. */
function sanitizeCount(value: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return 0;
  return Math.floor(value);
}

/**
 * Normalize a record's counters into device-attributed buckets plus derived
 * flat sums (lazy migration — see spec-counter-merge.md "deficit fold"):
 *
 *  1. Sanitize each bucket (NaN/negative → 0, floor, clamp c ≤ t).
 *  2. Sanitize the flat counters the same way (clamp correct ≤ total).
 *  3. Fold the flat-counter DEFICIT (flat − Σ buckets, clamped at 0) into the
 *     `legacy` bucket. A record with no `attempts` (or `attempts: {}`) is the
 *     Σ = 0 special case: the whole flat history becomes `legacy`. The
 *     general fold matters because old clients build records via spread —
 *     they increment flat counters while carrying a STALE pulled `attempts`
 *     map, leaving flat > Σ. Recomputing flat from buckets alone would
 *     undercount those attempts and violate non-reducing.
 *  4. Recompute flat = Σ buckets. This also handles the corrupt flat < Σ
 *     case by raising flat up to Σ — buckets are never reduced.
 *
 * Pure; never mutates the input. Exported: `updateWordSRS` uses it as the
 * write-path migration step, and tests assert against it directly.
 */
export function normalizeAttempts(record: WordProgress): {
  attempts: Record<string, AttemptBucket>;
  totalAttempts: number;
  correctAttempts: number;
} {
  const attempts: Record<string, AttemptBucket> = {};
  let bucketTotal = 0;
  let bucketCorrect = 0;
  const source = record.attempts;
  if (source) {
    for (const key in source) {
      const raw = source[key];
      // JSON from localStorage/cloud can hold a malformed bucket (null,
      // junk) despite the compile-time type — drop it, don't dereference.
      // Arrays are typeof 'object' too; without the explicit check they'd
      // sanitize into a phantom {t:0,c:0} bucket instead of being dropped.
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
      const t = sanitizeCount(raw.t);
      const c = Math.min(sanitizeCount(raw.c), t); // per-bucket invariant c ≤ t
      attempts[key] = { t, c };
      bucketTotal += t;
      bucketCorrect += c;
    }
  }

  const flatTotal = sanitizeCount(record.totalAttempts);
  const flatCorrect = Math.min(sanitizeCount(record.correctAttempts), flatTotal);

  // Deficit fold: flat history not yet attributed to any device bucket
  // belongs to `legacy`. Clamped at 0 — a flat count BELOW Σ buckets never
  // shrinks the buckets (flat gets recomputed up to Σ below instead).
  const deficitTotal = Math.max(0, flatTotal - bucketTotal);
  const deficitCorrect = Math.max(0, flatCorrect - bucketCorrect);
  if (deficitTotal > 0 || deficitCorrect > 0) {
    const legacy = attempts[LEGACY_DEVICE_ID] ?? { t: 0, c: 0 };
    const t = legacy.t + deficitTotal;
    const c = Math.min(legacy.c + deficitCorrect, t); // re-clamp after fold
    // Corrupt class flatCorrect > Σc with flatTotal ≤ Σt would fold to a
    // zero bucket (c clamped to t = 0) — never manufacture that junk key.
    if (t > 0) attempts[LEGACY_DEVICE_ID] = { t, c };
  }

  // Flat counters are DERIVED: always Σ buckets after normalization. An
  // all-zero record legitimately yields an empty `attempts` map (no `legacy`
  // key is manufactured for zero history).
  let totalAttempts = 0;
  let correctAttempts = 0;
  for (const key in attempts) {
    totalAttempts += attempts[key]!.t;
    correctAttempts += attempts[key]!.c;
  }

  return { attempts, totalAttempts, correctAttempts };
}

/**
 * Per-device-bucket max over the union of device keys. Exact (not lossy like
 * flat max) because only the owning device ever increments its own bucket —
 * so per-key max reconstructs each device's true count; `legacy` stays
 * max-merged, preserving today's semantics for pre-migration history.
 */
function mergeAttempts(
  a: Record<string, AttemptBucket>,
  b: Record<string, AttemptBucket>
): Record<string, AttemptBucket> {
  const merged: Record<string, AttemptBucket> = {};
  for (const key in a) {
    const other = b[key];
    const t = Math.max(a[key]!.t, other?.t ?? 0);
    const c = Math.min(Math.max(a[key]!.c, other?.c ?? 0), t);
    merged[key] = { t, c };
  }
  for (const key in b) {
    if (!(key in merged)) {
      merged[key] = { t: b[key]!.t, c: b[key]!.c };
    }
  }
  return merged;
}

/**
 * Merge two WordProgress records from potentially divergent devices.
 *
 * Rules (field-level, monotonic — never reduces):
 *  - state        → record with the higher SRS_RANK wins ("winner").
 *                   On ties (equal rank), fields are resolved commutatively:
 *                   lastReviewed = Math.max (most recent reviewed wins),
 *                   nextReview = Math.min (earlier scheduled wins),
 *                   cardIndex = Math.min (lower index is stable tiebreaker).
 *  - lastReviewed → from the winner; on tie: Math.max(a, b) (null = 0).
 *  - nextReview   → from the winner (clamped); on tie: Math.min(a, b).
 *  - attempts     → both sides normalized (deficit fold), then per-device-key
 *                   max over the union — EXACT cross-device counts, since
 *                   each device only ever grows its own bucket.
 *  - totalAttempts/correctAttempts → recomputed as Σ merged buckets (derived,
 *                   kept flat for UI reads).
 *  - correctStreak → Math.max(a, b). Unchanged on purpose — a recency-winner
 *                   rule was rejected as non-associative (see spec-counter-merge.md).
 *  - created      → Math.min(a.created, b.created).
 *  - cardIndex    → from the winner; on tie: Math.min(a, b).
 */
function mergeRecord(a: WordProgress, b: WordProgress): WordProgress {
  const rankA = SRS_RANK[a.state];
  const rankB = SRS_RANK[b.state];

  let state: WordProgress['state'];
  let lastReviewed: number | null;
  let nextReview: number;
  let cardIndex: number;

  if (rankA > rankB) {
    state = a.state;
    lastReviewed = a.lastReviewed;
    nextReview = a.nextReview;
    cardIndex = a.cardIndex;
  } else if (rankB > rankA) {
    state = b.state;
    lastReviewed = b.lastReviewed;
    nextReview = b.nextReview;
    cardIndex = b.cardIndex;
  } else {
    // Equal rank — commutative tiebreakers
    state = a.state; // same state value on both sides
    lastReviewed =
      a.lastReviewed === null && b.lastReviewed === null
        ? null
        : Math.max(a.lastReviewed ?? 0, b.lastReviewed ?? 0);
    nextReview = Math.min(a.nextReview, b.nextReview);
    cardIndex = Math.min(a.cardIndex, b.cardIndex);
  }

  // Normalize both sides (lazy migration of flat-only / stale-attempts
  // records into buckets), then take the exact per-bucket max.
  const attempts = mergeAttempts(
    normalizeAttempts(a).attempts,
    normalizeAttempts(b).attempts
  );
  let totalAttempts = 0;
  let correctAttempts = 0;
  for (const key in attempts) {
    totalAttempts += attempts[key]!.t;
    correctAttempts += attempts[key]!.c;
  }

  return {
    state,
    lastReviewed,
    nextReview: clampNextReview(nextReview),
    cardIndex,
    // Bucket-exact counters: flat sums are derived from the merged buckets,
    // never reduce, and (unlike flat max) don't drop concurrent increments.
    attempts,
    totalAttempts,
    correctAttempts,
    correctStreak: Math.max(a.correctStreak, b.correctStreak),
    // earliest known creation time
    created: Math.min(a.created, b.created)
  };
}

/**
 * One-side-only record: no counterpart to merge with, but it still gets the
 * clock-skew clamp AND attempt normalization — every record a merge emits
 * carries a consistent `attempts` map with flat == Σ buckets, regardless of
 * which path produced it (spec edge row "word present only on one side").
 * This is also what makes merge idempotent against normalization:
 * merge(r, r) === passThrough(r) for every record.
 */
function passThroughRecord(p: WordProgress): WordProgress {
  return {
    ...p,
    ...normalizeAttempts(p),
    nextReview: clampNextReview(p.nextReview)
  };
}

/**
 * Pure field-level merge of two WordProgressMaps.
 *
 * - PURE: no Supabase import, no mutation of inputs.
 * - O(n) single pass over the union of keys (n = |keys(a)| + |keys(b)|).
 * - Commutative, associative, idempotent, and non-reducing.
 */
export function mergeProgress(
  a: WordProgressMap,
  b: WordProgressMap
): WordProgressMap {
  const result: WordProgressMap = {};

  // Pass 1: all keys in `a`
  for (const key in a) {
    const pa = a[key]!;
    const pb = b[key];
    if (pb !== undefined) {
      result[key] = mergeRecord(pa, pb);
    } else {
      // Only in `a` — clamp + normalize on the way through
      result[key] = passThroughRecord(pa);
    }
  }

  // Pass 2: keys only in `b`
  for (const key in b) {
    if (!(key in result)) {
      result[key] = passThroughRecord(b[key]!);
    }
  }

  return result;
}
